import { FileText, Lock } from 'lucide-react'
import {
  formatoDeSueldo,
  etiquetaDeEstatus,
} from '@/features/rh/lib/datosDelCandidato'
import { experienciaDelCandidato } from '@/features/rh/lib/experienciaDelCandidato'
import {
  informacionGeneralDelCandidato,
  sinDatosConsolidados,
  type FuentesDeInformacionGeneral,
} from '@/features/rh/lib/informacionGeneralDelCandidato'

/** Un dato suelto dentro de una de las cajas. */
const Dato = ({ etiqueta, valor }: { etiqueta: string; valor: string }) => (
  <div>
    <p className="text-xs text-[#787878] mb-1">{etiqueta}</p>
    <p className="text-sm text-[#2F2F2F]">{valor}</p>
  </div>
)

const Caja = ({
  titulo,
  children,
}: {
  titulo: string
  children: React.ReactNode
}) => (
  <div className="bg-[#F8F9FB] rounded-xl p-5">
    <p className="text-sm font-semibold text-[#1E1E1F] mb-4">{titulo}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
      {children}
    </div>
  </div>
)

/**
 * Pestaña «Información general» de la ficha del candidato (RH-03).
 *
 * Reemplaza a la pestaña «Experiencia», que mostraba solo el resumen: es el
 * mismo lugar, con los datos que Dirección necesita para decidir. Dejar las
 * dos habría puesto el resumen profesional repetido en dos pestañas.
 *
 * Los datos salen de lo que la ficha ya trae —el candidato, su entrevista RH y
 * la vacante—, no de una consulta aparte. Así la pestaña funciona en cualquier
 * etapa del proceso y no solo mientras el candidato está en la ventana de
 * «aprobado por RH».
 */
export const InformacionGeneralDelCandidato = ({
  fuentes,
  puedeVerDatosDeDireccion,
  verCompensacion,
}: {
  fuentes: FuentesDeInformacionGeneral
  /**
   * Los sueldos y la ubicación son los datos que el ticket amarra al permiso
   * de Dirección. El resumen profesional no: ya era visible para todos antes
   * de esta pantalla y quitárselo a quien lo tenía sería una regresión.
   */
  puedeVerDatosDeDireccion: boolean
  /**
   * Los montos son aparte del permiso de Dirección: al solicitante se le
   * quitan aunque vea el resto de la ficha (SRH-319).
   */
  verCompensacion: boolean
}) => {
  const info = informacionGeneralDelCandidato(fuentes)

  return (
    <div className="bg-white rounded-xl shadow-sm border border-[#E0E0E0]">
      <div className="px-6 py-4 border-b border-[#E0E0E0]">
        <h2 className="text-base font-semibold text-[#1E1E1F]">
          Información general
        </h2>
      </div>

      <div className="p-6 space-y-4">
        <div className="bg-[#F8F9FB] rounded-xl p-5">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <h3 className="text-sm font-semibold text-[#1E1E1F]">
              Resumen profesional
            </h3>
          </div>
          <p className="text-sm text-[#2F2F2F] leading-relaxed whitespace-pre-wrap pl-6">
            {info.resumenProfesional ||
              'No se proporcionó descripción de experiencia.'}
          </p>
        </div>

        {!puedeVerDatosDeDireccion ? (
          <p className="flex items-center gap-2 text-sm text-[#787878] italic">
            <Lock className="w-4 h-4 flex-shrink-0" />
            Los datos de salarios y ubicación requieren permiso de Dirección.
          </p>
        ) : sinDatosConsolidados(info) ? (
          <p className="text-sm text-[#787878] italic">
            Todavía no se capturan los datos de la entrevista RH.
          </p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* La caja entera se va para quien no ve compensación (SRH-319):
                el servidor ya no le manda los montos, y dejarla pintada sería
                enseñarle tres etiquetas con guiones. */}
            {verCompensacion && (
            <Caja titulo="Salarios:">
              <Dato
                etiqueta="Salario anterior:"
                valor={formatoDeSueldo(info.salarioAnterior)}
              />
              <Dato
                etiqueta="Expectativa salarial:"
                valor={formatoDeSueldo(info.expectativaSalarial)}
              />
              {info.rangoDeLaVacante && (
                /* El rango es de la vacante, no del candidato: se ve igual en
                   todas las fichas de esa plaza. Se dice en la etiqueta para
                   que nadie lo lea como una propuesta hecha a esta persona. */
                <div className="sm:col-span-2">
                  <Dato
                    etiqueta="Rango autorizado de la vacante:"
                    valor={`${formatoDeSueldo(info.rangoDeLaVacante.min)} – ${formatoDeSueldo(info.rangoDeLaVacante.max)}`}
                  />
                </div>
              )}
            </Caja>
            )}

            <Caja titulo="Ubicación y disponibilidad:">
              <Dato
                etiqueta="Disponibilidad:"
                valor={info.disponibilidad || '—'}
              />
              <Dato etiqueta="Residencia:" valor={info.residencia || '—'} />
              <Dato
                etiqueta="Estatus laboral:"
                valor={etiquetaDeEstatus(info.situacionLaboral)}
              />
              <Dato
                etiqueta="Experiencia:"
                valor={experienciaDelCandidato(info.experienceYears)}
              />
            </Caja>
          </div>
        )}
      </div>
    </div>
  )
}
